import {
  BackSide,
  Color,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  Uniform
} from "three";
import type { UnderwaterVisualSettings } from "./VisualSettings";

export class CausticsWalls extends Mesh {

  private timeUniform = new Uniform(0);
  private scaleUniform = new Uniform(0.48);
  private speedUniform = new Uniform(0.5);
  private intensityUniform = new Uniform(1);
  private opacityUniform = new Uniform(1);
  private surfaceGlowUniform = new Uniform(1);
  private deepColorUniform = new Uniform(new Color("#06334d"));
  private shallowColorUniform = new Uniform(new Color("#2199c2"));
  private highlightColorUniform = new Uniform(new Color("#6bf5ff"));
  private surfaceGlowColorUniform = new Uniform(new Color("#146175"));

  constructor(roomSize: number, roomHeight: number, baseCameraHeight: number) {
    const radius = Math.max(roomSize, roomHeight) * 1.16;
    const geometry = new SphereGeometry(radius, 96, 48);
    const material = new MeshBasicMaterial({
      color: new Color('#16a8c2'),
      transparent: true,
      opacity: 0.42,
      side: BackSide,
      depthWrite: true,
    });

    super(geometry, material);

    material.onBeforeCompile = (shader) => {
      shader.defines = {
        DISTANCE: '1',
      }
      shader.uniforms.time = this.timeUniform;
      shader.uniforms.causticsScale = this.scaleUniform;
      shader.uniforms.causticsSpeed = this.speedUniform;
      shader.uniforms.causticsIntensity = this.intensityUniform;
      shader.uniforms.causticsOpacity = this.opacityUniform;
      shader.uniforms.surfaceGlowIntensity = this.surfaceGlowUniform;
      shader.uniforms.causticsDeepColor = this.deepColorUniform;
      shader.uniforms.causticsShallowColor = this.shallowColorUniform;
      shader.uniforms.causticsHighlightColor = this.highlightColorUniform;
      shader.uniforms.causticsSurfaceGlowColor = this.surfaceGlowColorUniform;
      shader.uniforms.roomHeight = new Uniform(roomHeight);
      shader.uniforms.baseCameraHeight = new Uniform(baseCameraHeight);

      shader.vertexShader = shader.vertexShader.replace('#include <common>', `
        #include <common>
        uniform float roomHeight;
        varying vec3 vPosition;
      `);

      shader.vertexShader = shader.vertexShader.replace('#include <worldpos_vertex>', /*glsl*/`
        #include <worldpos_vertex>
        
        vPosition = vec3( worldPosition );
      `);

      shader.fragmentShader = shader.fragmentShader.replace('#include <common>', `
        #include <common>
        uniform float time;
        varying vec3 vPosition;
        uniform float roomHeight;
        uniform float baseCameraHeight;
      `);

      shader.fragmentShader = shader.fragmentShader.replace('#include <color_fragment>', /*glsl*/`
        #include <color_fragment>

        float bottomFactor = smoothstep(-baseCameraHeight, roomHeight / 2.0, vPosition.y);

        diffuseColor.rgb = vec3(0.045, 0.13, 0.135) * 0.33;

        diffuseColor.a = mix(0.5, 0.0, bottomFactor);
        diffuseColor.a = mix(1.0, diffuseColor.a, smoothstep(-2.0, 2.0, vPosition.y));

      `);
    }
  }

  setSettings(settings: UnderwaterVisualSettings) {
    this.scaleUniform.value = settings.causticsScale;
    this.speedUniform.value = settings.causticsSpeed;
    this.intensityUniform.value = settings.causticsIntensity;
    this.opacityUniform.value = settings.causticsOpacity;
    this.surfaceGlowUniform.value = settings.surfaceGlowIntensity;
    this.deepColorUniform.value.set(settings.causticsDeepColor);
    this.shallowColorUniform.value.set(settings.causticsShallowColor);
    this.highlightColorUniform.value.set(settings.causticsHighlightColor);
    this.surfaceGlowColorUniform.value.set(settings.causticsSurfaceGlowColor);
    this.scale.setScalar(settings.causticsRadiusScale);
  }

  update(time: number) {
    this.timeUniform.value = time;
  }
}
